import type { UserAppModel, UserModel } from '@/shared/generated'
import type { InitType } from '@intercom/messenger-js-sdk/dist/types'

import { useEffect } from 'react'
import DrawerManagerProvider from '@/app/providers/DrawerManagerProvider'
import ModalManagerProvider from '@/app/providers/ModalManagerProvider'
import SocketProvider from '@/app/providers/SocketProvider'
import { setGroup, setUserId } from '@amplitude/analytics-browser'
import IntercomSDK, { shutdown } from '@intercom/messenger-js-sdk'
import { OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
	createFileRoute,
	Navigate,
	Outlet,
	redirect,
	useRouteContext,
} from '@tanstack/react-router'

import { getLoginUrl, sessionQueries } from '@/entities/session'
import { getUsername, userQueries } from '@/entities/user'

import { AppSidebar } from '@/widgets/app-sidebar'

import { NotFoundPage } from '@/shared/components/common/NotFoundPage'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Clarifier } from '@/shared/components/ui/clarify'
import { Confirmer } from '@/shared/components/ui/confirmer'
import { Layout } from '@/shared/components/ui/layout'
import { SidebarInset, SidebarProvider } from '@/shared/components/ui/sidebar'
import { Toaster } from '@/shared/components/ui/sonner'
import { env } from '@/shared/config/env'
import { ApplicationProvider, store } from '@/shared/contexts/app'
import { ampli } from '@/shared/lib/ampli'
import { multiProvider } from '@/shared/lib/flags'
import { queryClient } from '@/shared/lib/query-client'
import Sentry from '@/shared/lib/sentry'
import { getProject } from '@/shared/tools/utils'

import { initializeAmpli } from '../../config/ampli'

function RouteComponent() {
	const user = useRouteContext({ from: '/_app', select: (data) => data.user })

	useEffect(() => {
		initializeAmpli()
		initOpenFeature(user!)
		initSentry(user!)
		initAmplitude(user!)
		initPreferences()
	}, [user])

	if (!isAppEnabled(getApp(user))) {
		return <Navigate to='/denied' replace />
	}

	return (
		<ApplicationProvider>
			<ErrorBoundary>
				<OpenFeatureProvider>
					<ModalManagerProvider>
						<DrawerManagerProvider>
							<SocketProvider>
								<SidebarProvider className='h-screen' defaultOpen={false}>
									<AppSidebar />
									<SidebarInset className='overflow-hidden'>
										<Layout className='h-screen overflow-hidden'>
											<ErrorBoundary>
												<Outlet />
											</ErrorBoundary>
										</Layout>
									</SidebarInset>
								</SidebarProvider>
							</SocketProvider>
							<Confirmer />
							<Clarifier />
							<Toaster expand={true} />
							<ReactQueryDevtools />
						</DrawerManagerProvider>
					</ModalManagerProvider>
					<SupportChat user={user} />
				</OpenFeatureProvider>
			</ErrorBoundary>
		</ApplicationProvider>
	)
}

const canUseDOM = !!(
	typeof window !== 'undefined' &&
	window.document &&
	window.document.createElement
)
const getApp = (user: UserModel) =>
	user.apps.find((app) => app.name === 'pricing_platform')
const isAppEnabled = (app?: UserAppModel) => app?.enabled

const intercomSettings = (user: UserModel, projectId?: string) => ({
	user_id: user.id,
	email: user.email,
	name: getUsername(user),
	project: projectId,
	project_type: 'platform',
	widget: { activator: '.fa-comment' },
})

const Intercom = ({
	appID,
	...props
}: { appID: string } & Partial<InitType>) => {
	useEffect(() => {
		IntercomSDK({ ...props, app_id: appID })

		return () => {
			if (!canUseDOM) return
			shutdown()
		}
	}, [])

	return null
}
const SupportChat = ({ user }: { user: UserModel }) => {
	const projectId = getProject()

	return (
		user && (
			<Intercom
				appID={env.VITE_INTERCOM_APP_ID!}
				{...intercomSettings(user, projectId)}
			/>
		)
	)
}

const initOpenFeature = (user: UserModel) => {
	const projectId = getProject()
	OpenFeature.setProviderAndWait(multiProvider, {
		targetingKey: user.id,
		client: projectId,
		email: user.email || '',
		app: 'platform',
	})
}
const initSentry = (user: UserModel) => {
	const roles = getApp(user)?.role_names
	const username = getUsername(user as UserModel)
	Sentry.setUser({ roles, email: user?.email, username, id: user?.id })
}
const initPreferences = async () => {
	try {
		const pref = await queryClient.ensureQueryData(userQueries.preferences())
		store.setState({ dateFormat: pref?.date_format || 'EU' })
	} catch (error) {
		Sentry.captureException(error)
	}
}
const initAmplitude = (user: UserModel) => {
	const projectId = getProject()
	const theme = localStorage.getItem('theme')
	const roles = getApp(user)?.role_names
	setUserId(user.id)
	ampli.identify(user.id, {
		userEmail: user.email,
		clientName: projectId,
		userFirstName: user.first_name!,
		userLastName: user.last_name!,
		userRole: roles,
		uiLocale: navigator.language,
		uiScreenW: window.innerWidth,
		uiScreenH: window.innerHeight,
		uiTheme: theme || 'light',
	})
	setGroup('projectId', projectId)
}

export const Route = createFileRoute('/_app')({
	component: RouteComponent,
	notFoundComponent: () => <NotFoundPage />,
	beforeLoad: async ({ context: { queryClient } }) => {
		try {
			// Awaiting makes the guard race-free; resolves from cache after the first navigation.
			return {
				user: await queryClient.query(sessionQueries.currentUser()),
			}
		} catch {
			// `reloadDocument`: the login URL is served by the dev proxy, not a client route.
			throw redirect({ href: getLoginUrl(), reloadDocument: true })
		}
	},
})
